import { queryOptions, useQuery, useQueryClient } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"
import { api, type ListResponse } from "@/api/client"
import type { GitImportResult } from "@/api/generated/GitImportResult"
import type { SkillPreset } from "@/api/generated/SkillPreset"
import type { SkillRow } from "@/api/generated/SkillRow"
import type { SkillSummary } from "@/api/generated/SkillSummary"
import { useApiMutation } from "@/api/mutation"

export const skillKeys = {
  all: ["skills"] as const,
  list: () => [...skillKeys.all, "list"] as const,
  detail: (id: number) => [...skillKeys.all, "detail", id] as const,
  presets: ["skill-presets"] as const,
}

export const skillsQuery = () =>
  queryOptions({
    queryKey: skillKeys.list(),
    queryFn: ({ signal }) => api.get<ListResponse<SkillSummary>>("/skills", { signal }),
    select: (r) => r.items,
  })

export const useSkills = () => useQuery(skillsQuery())

export const skillQuery = (id: number) =>
  queryOptions({
    queryKey: skillKeys.detail(id),
    queryFn: ({ signal }) => api.get<SkillRow>(`/skills/${id}`, { signal }),
  })

export const useSkill = (id: number | undefined) => useQuery({ ...skillQuery(id ?? 0), enabled: id != null })

export interface SkillInput {
  name?: string
  slug?: string
  description?: string
  body?: string
  enabled?: boolean
}

export function useCreateSkill() {
  const { t } = useTranslation()
  return useApiMutation<SkillRow, SkillInput>({
    mutationFn: (body) => api.post("/skills", body),
    invalidates: [skillKeys.all],
    successToast: (s) => t("studio.skills.created", { name: s.name }),
    errorToast: "unlessFields",
  })
}

export function useUpdateSkill() {
  const { t } = useTranslation()
  const qc = useQueryClient()
  return useApiMutation<SkillRow, { id: number; silent?: boolean } & SkillInput>({
    mutationFn: ({ id, silent: _s, ...body }) => api.patch(`/skills/${id}`, body),
    onSuccess: (s) => qc.setQueryData(skillKeys.detail(s.id), s),
    invalidates: [skillKeys.all],
    successToast: (s, v) => !v.silent && t("studio.skills.updated", { name: s.name }),
    errorToast: "unlessFields",
  })
}

export function useDeleteSkill() {
  const { t } = useTranslation()
  return useApiMutation<void, Pick<SkillSummary, "id" | "name">>({
    mutationFn: (s) => api.delete(`/skills/${s.id}`),
    invalidates: [skillKeys.all, skillKeys.presets, ["agents"]],
    successToast: (_d, s) => t("studio.skills.deleted", { name: s.name }),
  })
}

export function useImportSkillMarkdown() {
  const { t } = useTranslation()
  return useApiMutation<SkillRow, { markdown: string; name?: string }>({
    mutationFn: (body) => api.post("/skills/import/markdown", body),
    invalidates: [skillKeys.all],
    successToast: (s) => t("studio.skills.imported", { name: s.name }),
  })
}

export interface GitImportInput {
  repo_url: string
  branch?: string
  path?: string
  disable_missing?: boolean
}

export function useImportSkillsGit() {
  return useApiMutation<GitImportResult, GitImportInput>({
    mutationFn: (body) => api.post("/skills/import/git", body),
    invalidates: [skillKeys.all],
    // the dialog shows field and upstream (502) errors inline
    errorToast: false,
  })
}

// ------------------------------------------------------------------ presets

export const presetsQuery = () =>
  queryOptions({
    queryKey: skillKeys.presets,
    queryFn: ({ signal }) => api.get<ListResponse<SkillPreset>>("/skill-presets", { signal }),
    select: (r) => r.items,
  })

export const useSkillPresets = () => useQuery(presetsQuery())

export interface PresetInput {
  name?: string
  description?: string
  skill_ids?: number[]
}

export function useCreatePreset() {
  const { t } = useTranslation()
  return useApiMutation<SkillPreset, PresetInput>({
    mutationFn: (body) => api.post("/skill-presets", body),
    invalidates: [skillKeys.presets],
    successToast: (p) => t("studio.skills.presets.created", { name: p.name }),
    errorToast: "unlessFields",
  })
}

export function useUpdatePreset() {
  const { t } = useTranslation()
  return useApiMutation<SkillPreset, { id: number } & PresetInput>({
    mutationFn: ({ id, ...body }) => api.patch(`/skill-presets/${id}`, body),
    invalidates: [skillKeys.presets],
    successToast: (p) => t("studio.skills.presets.updated", { name: p.name }),
    errorToast: "unlessFields",
  })
}

export function useDeletePreset() {
  const { t } = useTranslation()
  return useApiMutation<void, SkillPreset>({
    mutationFn: (p) => api.delete(`/skill-presets/${p.id}`),
    invalidates: [skillKeys.presets],
    successToast: (_d, p) => t("studio.skills.presets.deleted", { name: p.name }),
  })
}
